<template>
    <h2><strong>Employment Details</strong></h2>
    <DynamicForm  :fields="formFields" :flex_basis="flex_basis" :flex_basis_percentage="flex_basis_percentage"  @handleReset="handleReset"> 
        <template v-slot:additional-content>
            <div class="border border-slate-200 rounded relative py-1.5 mt-3 px-2 min-h-[280px]">
                <h1 class="font-bold absolute top-[-13px] left-5 bg-white">Deductions & Benefits</h1>
                <div class="tabs pt-2">
                    <button v-for="(tab, index) in tabs" :key="tab" :class="['tab', { active: activeTab === index }]"@click="selectTab(index)">
                        {{ tab }}
                    </button>
                </div>
                <div class="tab-content">
                    <div v-show="activeTab == 0">
                        <DynamicForm :fields="additionalFields" :flex_basis="additional_flex_basis" :flex_basis_percentage="additional_flex_basis_percentage" @handleReset="handleReset"/>
                    </div>
                    <div v-show="activeTab == 1">
                        <div class="text-left p-2">
                            <SearchableDropdown 
                                :key="deducComponentKey"
                                :options="deductionArr"
                                :dropdownWidth="deductionsDropdownWidth"
                                :searchPlaceholder="deductionsSearchPlaceholder"
                                @option-selected="handleSelectedDeduction"
                                @fetchData="fetchData"
                            />
                        </div>                      
                        <DynamicTable :key="tableKey" :columns="deductionColumns" :rows="computedDeductionRows" :idField="idFieldDeduction" :actions="actionDeductions" @action-click="deleteDeduction" :rightsModule="rightsModule" />
                    </div>
                    <div v-show="activeTab == 2">
                        <div class="text-left p-2">
                            <SearchableDropdown 
                                :key="benComponentKey"
                                :options="benefitArr"
                                :dropdownWidth="benefitsDropdownWidth"
                                :searchPlaceholder="benefitsSearchPlaceholder"
                                @option-selected="handleSelectedBenefit"
                                @fetchData="fetchData"
                            />
                        </div>                      
                        <DynamicTable :key="tableKey" :columns="benefitColumns" :rows="computedBenefitRows" :idField="idFieldBenefit" :actions="actionBenefits" @action-click="deleteBenefit" :rightsModule="rightsModule" />                     
                    </div>
                </div>
            </div>
        </template>
    </DynamicForm>
    
</template>

<script>
import { defineComponent, ref, onBeforeMount, onMounted, computed, watch, reactive } from 'vue';
import DynamicForm from '@/components/NewDynamicForm.vue';
import SearchableDropdown from '@/components/SearchableDropdown.vue';
import DynamicTable from '@/components/DynamicTable.vue';
import { useStore } from "vuex";
import { useToast } from "vue-toastification";

export default defineComponent({
    name: 'Deduction_Details',
    components:{
        DynamicForm, SearchableDropdown, DynamicTable
    },
    props:{
        formFields: Array,
        additionalFields: Array,
        deductionRows:{
            type: Array,
            default:() => [],
        },
        benefitRows:{
            type: Array,
            default:() => [],
        },
    },
    emits: ['update-form'],
    setup(props,{emit}){
        const store = useStore();
        const toast = useToast();
        const loader = ref('none');
        const tabs = ref(['Statutory Deductions','Recurrent Earnings/Deductions','Employee Benefits']);
        const rightsModule = ref('HR');
        const activeTab = ref(0);
        const mainComponentKey = ref(0);
        const bankComponentKey = ref(0);
        const deducComponentKey = ref(0);
        const benComponentKey = ref(0);
        const tableKey = ref(0);
        const statTableKey = ref(0);
        const deducTableKey = ref(0);
        const currencyComponentKey = ref(0);
        const payComponentKey = ref(0);
        const companyID = computed(()=> store.state.userData.company_id);
        const isEditing = computed(()=> store.state.Employees.isEditing);
        const flex_basis = ref('');
        const flex_basis_percentage = ref('');
        const additional_flex_basis = ref('');
        const additional_flex_basis_percentage = ref('');
        const selectedEmployee = computed(()=> store.state.Employees.selectedEmployee);
        const selectedCurrency = computed(()=> store.state.Employees.selectedCurrency);
        const selectedBank = computed(()=> store.state.Employees.selectedBank);
        const selectedPayGroup = computed(()=> store.state.Employees.selectedPayGroup);
        const employeeDeductions = computed(()=> store.state.Employees.employeeDeductions);
        const employeeBenefits = computed(()=> store.state.Employees.employeeBenefits);
        const currencyArray = computed(() => store.state.Currencies.currencyArr);
        const bankArray = computed(() => store.state.Banks.bankArr);
        const payGroupArray = computed(() => store.state.Pay_Groups.groupArr);
        const currencyID = ref('');
        const payGroupID = ref('');
        const bankID = ref('');
        const deductionArr = computed({
            get: () => store.state.Deductions.deductionArr,
        });
        const deductionsDropdownWidth = ref('400px');
        const deductionsSearchPlaceholder = ref('Select Earning/Deduction');
        const deductionColumns = ref([
            {label: "Name", key:"deduction_name", type: "text", editable: false},
            {label: "Type", key:"deduction_type", type: "text", editable: false},
            {label: "Amount", key: "default_value", type: "number", editable: true},
        ])

        const localDeductionRows = ref([...props.deductionRows]);
        const computedDeductionRows = computed(() => {
            return store.state.Deductions.deductionArray;
        });

        const idFieldDeduction = 'deduction_id';
        const actionDeductions = ref([
            {name: 'delete', icon: 'fa fa-minus-circle', title: 'Remove Deduction', rightName: 'Adding Employees'},
        ])
        const benefitArr = computed(() => store.state.Employee_Benefits.benefitArr);
        const benefitsDropdownWidth = ref('400px');
        const benefitsSearchPlaceholder = ref('Select Benefit');
        const benefitColumns = ref([
            {label: "Benefit Type", key:"benefit_type", type: "text"},
            {label: "Default Amount/Rate", key:"amount", type: "text"},
            {label: "Asset Value", key:"asset_value", type: "number", editable: true},
            {label: "Amount/Rate", key: "benefit_amount", type: "number", editable: true},
        ])

        const localBenefitRows = ref([...props.benefitRows]);
        const computedBenefitRows = computed(() => {
            return store.state.Employee_Benefits.benefitArray;
        });

        const idFieldBenefit = 'benefit_employee_id';
        const actionBenefits = ref([
            {name: 'delete', icon: 'fa fa-minus-circle', title: 'Remove Benefit', rightName: 'Adding Employees'},
        ])

        const showLoader = () =>{
            loader.value = "block";
        };
        const hideLoader = () =>{
            loader.value = "none";
        };

        watch([currencyID], () => {
            if(currencyID.value){
                formFields.value[10].value = currencyID.value;
            }


        }, { immediate: true });
        const formFields = ref(props.formFields);
        const currencyValue = computed(() => {
           return (selectedEmployee.value && selectedEmployee.value.employee_currency && !currencyID.value) ? selectedEmployee.value.employee_currency.currency_id : currencyID.value;
        });
        const bankValue = computed(() => {
            return (selectedEmployee.value && selectedEmployee.value.employee_bank && !bankID.value) ? selectedEmployee.value.employee_bank[0].bank_id : bankID.value;
        });
        const payValue = computed(() => {
            return (selectedEmployee.value && selectedEmployee.value.employee_pay_group && !payGroupID.value) ? selectedEmployee.value.employee_pay_group[0].pay_group_id : payGroupID.value;
        });
        const accName = computed(() => {
            return (selectedEmployee.value && selectedEmployee.value.employee_bank) ? selectedEmployee.value.employee_bank[0].account_name : "-";
        });
        const accNumber = computed(() => {
            return (selectedEmployee.value && selectedEmployee.value.employee_bank) ? selectedEmployee.value.employee_bank[0].account_number : "0";
        });
        const updateFormFields = () => {
            formFields.value = [
                { type: 'date', name: 'employment_start_date',label: "Employment Date", value: selectedEmployee.value?.employment_start_date || '', required: true },
                { type: 'date', name: 'employment_end_date',label: "Employment End Date", value: selectedEmployee.value?.employment_end_date || '', required: false },
                { type: 'text', name: 'basic_pay',label: "Basic Pay", value: selectedEmployee.value?.basic_pay || '0', required: true },
                { type: 'dropdown', name: 'employment_status',label: "Employment Type", value: selectedEmployee.value?.employment_status || '', placeholder: "", required: true, options: [{ text: 'Full-time Permanent', value: 'Full-time Permanent' }, { text: 'Part-time Permanent', value: 'Part-time Permanent' }, { text: 'Consultant', value: 'Consultant' },{ text: 'Casual', value: 'Casual' }, { text: 'Full-time Contract', value: 'Full-time Contract' }, { text: 'Part-time Contract', value: 'Part-time Contract' },{ text: 'Probation', value: 'Probation' }, { text: 'Internship', value: 'Internship' }] },
                { type: 'dropdown', name: 'payment_method',label: "Payment Method", value: selectedEmployee.value?.payment_method || '', placeholder: "", required: false, options: [{ text: 'Cash', value: 'Cash' }, { text: 'Mpesa', value: 'Mpesa' }, { text: 'Bank Deposit', value: 'Bank Deposit' },{ text: 'Cheque', value: 'Cheque' }, { text: 'Check-off', value: 'Check-off' }, { text: 'RTGS', value: 'RTGS' },{ text: 'EFT', value: 'EFT' }, { text: 'Not Applicable', value: 'Not Applicable' }] },
                {  
                    type:'search-dropdown', label:"Bank", value: bankValue.value, componentKey: bankComponentKey,
                    selectOptions: bankArray, optionSelected: handleSelectedBank, required: false,
                    searchPlaceholder: 'Select Bank...', dropdownWidth: '500px', updateValue: selectedBank.value,
                    clearSearch: clearSelectedBank
                },
                { type: 'text', name: 'account_name',label: "Account Name", value: accName.value, required: false },
                { type: 'text', name: 'account_number',label: "Account No", value: accNumber.value, required: false },
                { type: 'text', name: 'insurance_premium',label: "Insurance Premium", value: selectedEmployee.value?.insurance_premium || '0', required: false },
                { type: 'text', name: 'insurance_relief',label: "Insurance Relief", value: selectedEmployee.value?.insurance_relief || '0', required: false },
                {  
                    type:'search-dropdown', label:"Currency", value: currencyValue.value, componentKey: currencyComponentKey,
                    selectOptions: currencyArray, optionSelected: handleSelectedCurrency, required: true,
                    searchPlaceholder: 'Select Currency...', dropdownWidth: '500px', updateValue: selectedCurrency.value,
                    clearSearch: clearSelectedCurrency
                },
                {  
                    type:'search-dropdown', label:"Pay Group", value: payValue.value, componentKey: payComponentKey,
                    selectOptions: payGroupArray, optionSelected: handleSelectedPayGroup, required: false,
                    searchPlaceholder: 'Select Pay Group...', dropdownWidth: '500px', updateValue: selectedPayGroup.value,
                    clearSearch: clearSelectedPayGroup
                },
               ];
               emitUpdatedFields();
        };
        const additionalFields = ref(props.additionalFields);
        const updateAdditionalFormFields = () => {
            additionalFields.value = [
                { type: 'dropdown', name: 'deduct_shif',label: "Deduct SHIF", value: selectedEmployee.value?.deduct_shif || 'No', placeholder: "", required: true, options: [{ text: 'Yes', value: 'Yes' }, { text: 'No', value: 'No' }] },
                { type: 'text', name: 'shif_number',label: "SHIF No", value: selectedEmployee.value?.shif_number || '-', required: false },
                {required:false},
                { type: 'dropdown', name: 'deduct_nssf',label: "Deduct NSSF", value: selectedEmployee.value?.deduct_nssf || 'No', placeholder: "", required: true, options: [{ text: 'Yes', value: 'Yes' }, { text: 'No', value: 'No' }] },
                { type: 'text', name: 'nssf_number',label: "NSSF No", value: selectedEmployee.value?.nssf_number || '-', required: false },
                {required:false}
            ];
            emitUpdatedFields();
        };
        const emitUpdatedFields = () => {
            emit('update-form', formFields.value, additionalFields.value, localDeductionRows.value, localBenefitRows.value);
        };
        watch([bankID, payGroupID], () => {
            if (bankID.value != "") {
                formFields.value[5].value = bankID.value
            }
            if (payGroupID.value != "") {
                formFields.value[11].value = payGroupID.value
            }
        }, { immediate: true });
        const handleReset = () =>{
            for(let i=0; i < formFields.value.length; i++){
                formFields.value[i].value = '';
            }
  
            for(let i=0; i < additionalFields.value.length; i++){
                additionalFields.value[i].value = '';
            }
            currencyID.value = '';
            payGroupID.value = '';
            bankID.value = '';
            store.dispatch('Deductions/updateState',{deductionArray: []})
            emit('reset-employment-details')
        }

        watch([selectedEmployee], () => {
            
        }, { immediate: true });
        const handleSelectedBank = async(option) =>{
            await store.dispatch('Banks/handleSelectedBank', option)
            bankID.value = store.state.Banks.bankID;
        }
        const clearSelectedBank = async() =>{
            await store.dispatch('Banks/updateState', {bankID: ''});
            bankID.value = store.state.Banks.bankID;
        }

        const fetchBanks = async() =>{
            await store.dispatch('Banks/fetchBanks', {company:companyID.value})
        };

        const handleSelectedCurrency = async(option) =>{
            await store.dispatch('Currencies/handleSelectedCurrency', option)
            currencyID.value = store.state.Currencies.currencyID;
            if(selectedEmployee.value){
                selectedEmployee.value.employee_currency.currency_id = currencyID.value;
            }
        }
        const clearSelectedCurrency = async() =>{
            await store.dispatch('Currencies/updateState', {currencyID: ''});
            currencyID.value = store.state.Currencies.currencyID;
        }

        const fetchCurrencies = async() =>{
            await store.dispatch('Currencies/fetchCurrencies', {company:companyID.value})
        };

        const handleSelectedPayGroup = async(option) =>{
            await store.dispatch('Pay_Groups/handleSelectedPayGroup', option)
            payGroupID.value = store.state.Pay_Groups.groupID;
        }
        const clearSelectedPayGroup = async() =>{
            await store.dispatch('Pay_Groups/updateState', {groupID: ''});
            payGroupID.value = store.state.Pay_Groups.groupID;
        }

        const fetchPayGroups = async() =>{
            await store.dispatch('Pay_Groups/fetchPayGroups', {company:companyID.value})
        };
        
        watch([selectedEmployee, selectedCurrency, selectedBank, selectedPayGroup, employeeDeductions, employeeBenefits], () => {
            if(employeeDeductions.value){
                deducComponentKey.value += 1;
                store.dispatch('Deductions/updateState',{deductionArray: employeeDeductions.value})
                tableKey.value += 1;
            }
            if(employeeBenefits.value){
                benComponentKey.value += 1;
                store.dispatch('Employee_Benefits/updateState',{benefitArray: employeeBenefits.value})
                tableKey.value += 1;
            }
            if (selectedEmployee.value && selectedCurrency.value  && selectedPayGroup.value && selectedBank.value) {
                bankComponentKey.value += 1;
                currencyComponentKey.value += 1;
                payComponentKey.value += 1;
                formFields.value[6].value = selectedEmployee.value.employee_bank[0].account_name;
                formFields.value[7].value = selectedEmployee.value.employee_bank[0].account_number;
                updateFormFields();
                updateAdditionalFormFields();
            }else if (selectedEmployee.value && selectedCurrency.value  && selectedPayGroup.value && selectedBank.value) {
                bankComponentKey.value += 1;
                currencyComponentKey.value += 1;
                payComponentKey.value += 1;
                deducComponentKey.value += 1;
                formFields.value[6].value = selectedEmployee.value.employee_bank[0].account_name;
                formFields.value[7].value = selectedEmployee.value.employee_bank[0].account_number;
                updateFormFields();
                updateAdditionalFormFields();
            }
            else if(selectedEmployee.value  && selectedCurrency.value && selectedPayGroup.value){
                currencyComponentKey.value += 1;
                payComponentKey.value += 1;
                updateFormFields();
                updateAdditionalFormFields();
            }
            else if(selectedEmployee.value  && selectedCurrency.value  && selectedBank.value){
                currencyComponentKey.value += 1;
                bankComponentKey.value += 1;
                formFields.value[6].value = selectedEmployee.value.employee_bank[0].account_name;
                formFields.value[7].value = selectedEmployee.value.employee_bank[0].account_number;
                updateFormFields();
                updateAdditionalFormFields();
            }
            else if(selectedEmployee.value  && selectedCurrency.value){
                currencyComponentKey.value += 1;
                updateFormFields();
                updateAdditionalFormFields();
            }
        }, { immediate: true });

        
        watch(() => store.state.Deductions.deductionArray, (newVal) => {
            if (newVal) {
                localDeductionRows.value = [...newVal];
                tableKey.value += 1;
                emitUpdatedFields();
            }
        },{ immediate: true });
        watch(() => store.state.Employee_Benefits.benefitArray, (newVal) => {
            if (newVal) {
                localBenefitRows.value = [...newVal];
                tableKey.value += 1;
                emitUpdatedFields();
            }
        },{ immediate: true });
         
        const selectTab = (index) => {
            activeTab.value = index;
        };
        
        const handleSelectedDeduction = async(option) =>{
            await store.dispatch('Deductions/handleSelectedDeduction', option);
            deducComponentKey.value += 1;
        }
        const deleteDeduction = (rowIndex, action, row) =>{
            store.dispatch('Deductions/removeDeduction', rowIndex);
            tableKey.value += 1;
        }
        const handleSelectedBenefit = async(option) =>{
            await store.dispatch('Employee_Benefits/handleSelectedBenefit', option);
            benComponentKey.value += 1;
        }
        const deleteBenefit = (rowIndex, action, row) =>{
            store.dispatch('Employee_Benefits/removeBenefit', rowIndex);
            tableKey.value += 1;
        }
        onBeforeMount(()=>{ 
            fetchBanks();
            fetchCurrencies();
            fetchPayGroups();
            store.dispatch('Deductions/fetchDeductions',{company:companyID.value});
            store.dispatch('Employee_Benefits/fetchEmployeeBenefits',{company:companyID.value});
            updateFormFields();
            updateAdditionalFormFields();
            currencyComponentKey.value += 1;
            payComponentKey.value += 1;
            bankComponentKey.value += 1;
            deducComponentKey.value += 1;
            benComponentKey.value += 1;
            flex_basis.value = '1/4';
            flex_basis_percentage.value = '25';
            additional_flex_basis.value = '1/4';
            additional_flex_basis_percentage.value = '25';
        })
        onMounted(()=>{
            emitUpdatedFields();
        })

        return{
            emitUpdatedFields, computedDeductionRows,computedBenefitRows,tabs, selectTab,activeTab, bankComponentKey,deducComponentKey,benComponentKey, formFields, additionalFields,flex_basis, 
            flex_basis_percentage, additional_flex_basis,additional_flex_basis_percentage, mainComponentKey,handleReset, isEditing, loader, showLoader, hideLoader,
            tableKey, deductionArr, deductionsDropdownWidth, deductionsSearchPlaceholder, deductionColumns,  idFieldDeduction, actionDeductions, deleteDeduction, handleSelectedDeduction,
            benefitArr,benefitsDropdownWidth,benefitsSearchPlaceholder,benefitColumns,idFieldBenefit,actionBenefits,handleSelectedBenefit,deleteBenefit,
            statTableKey,rightsModule
        }
    }
})
</script>

<style scoped>
.tabs {
    display: flex;
    border-bottom: 1px solid #ccc;
}
.tab {
    padding: 2px 20px 2px 20px;
    cursor: pointer;
}

.tab.active {
    border-bottom: 2px solid #000;
}

.tab-content {
    padding: 3px;
    margin-top: 10px !important;
}
</style>